import { CheckCircle2, Info, Mail, MoreHorizontal, type LucideIcon } from 'lucide-react'
import { useState, type KeyboardEvent, type ReactNode } from 'react'
import { Link } from 'react-router'
import { RichTextEditor } from '@/components/RichTextEditor'
import { RichTextView } from '@/components/RichTextView'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { isRichTextEmpty, richTextToPlain, trimRichText } from '@/lib/richText'
import { toast } from '@/lib/toast'
import { toastUndo } from '@/lib/toastUndo'
import { cn } from '@/lib/utils'
import { todayIso } from '../dates'
import { shortDay } from '../record/format'
import { ACTIVITY_ICONS, ACTIVITY_LABELS } from '../record/labels'
import { deleteActivity, restoreActivity, updateActivity } from './api'
import { IngesprokenBadge } from '../inspreken/IngesprokenBadge'
import { MailThreadItem } from './MailThreadItem'
import type { ActivityPatch, RelationRef, TimelineActivity, TimelineItem as TimelineItemData } from './types'
import { itemKey } from './useTimeline'

/** Longer than this (or more than four lines) a body is clamped behind "Meer". */
const CLAMP_CHARS = 320
const CLAMP_LINES = 4

const menuItemClass = 'rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted max-md:min-h-11'

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** What the editor starts from: the rich text, or the old plain text as one paragraph. */
function editableBody(item: TimelineActivity): string {
  if (item.body_html) return item.body_html
  return item.legacy_text ? `<p>${escapeHtml(item.legacy_text).replace(/\r?\n/g, '<br>')}</p>` : ''
}

function normalizeBody(html: string): string | null {
  return isRichTextEmpty(html) ? null : trimRichText(html)
}

function Row({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground">
        <Icon className="size-3.5" aria-hidden />
      </span>
      {children}
    </li>
  )
}

/**
 * Inline editing of an activity: the date (not for a note), the title or subject (not for a
 * note) and the text. Only what changed is sent; an edit that changes nothing just closes.
 */
function ActivityEditForm({
  item,
  onCancel,
  onSaved,
}: {
  item: TimelineActivity
  onCancel: () => void
  onSaved: (updated: TimelineActivity) => void
}) {
  const isNote = item.type === 'note'
  const initialBody = editableBody(item)
  const [date, setDate] = useState(item.occurred_at)
  const [title, setTitle] = useState(item.title ?? '')
  const [body, setBody] = useState(initialBody)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const canSave = !busy && (!isNote || !isRichTextEmpty(body))

  async function submit() {
    if (!canSave) return
    const patch: ActivityPatch = {}
    if (!isNote) {
      if (date && date !== item.occurred_at) patch.occurred_at = date
      const nextTitle = title.trim() || null
      if (nextTitle !== (item.title ?? null)) patch.title = nextTitle
    }
    const nextBody = normalizeBody(body)
    if (nextBody !== normalizeBody(initialBody)) patch.body_html = nextBody
    if (Object.keys(patch).length === 0) {
      onCancel()
      return
    }
    setBusy(true)
    setError(null)
    try {
      onSaved(await updateActivity(item.id, patch))
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Opslaan mislukt.')
    } finally {
      setBusy(false)
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault()
      onCancel()
    } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      void submit()
    }
  }

  return (
    <div className="flex flex-col gap-2" onKeyDown={onKeyDown}>
      {isNote ? null : (
        <div className="flex flex-wrap gap-2">
          <Input
            type="date"
            aria-label="Datum"
            max={todayIso()}
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="w-40 max-md:h-11 max-md:text-base"
          />
          <Input
            aria-label={item.type === 'email' ? 'Onderwerp' : 'Titel'}
            maxLength={200}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            className="min-w-48 flex-1 max-md:h-11 max-md:text-base"
          />
        </div>
      )}
      <RichTextEditor format="html" value={body} onChange={(value) => setBody(value)} ariaLabel="Tekst" />
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Annuleren
        </Button>
        <Button type="button" size="sm" disabled={!canSave} onClick={() => void submit()}>
          Opslaan
        </Button>
      </div>
    </div>
  )
}

function ActivityLine({
  item,
  relation,
  expandAll,
  onReplace,
  onRemove,
  onReload,
  onChanged,
}: {
  item: TimelineActivity
  relation: RelationRef
  expandAll: boolean
  onReplace: (item: TimelineItemData) => void
  onRemove: (key: string) => void
  onReload: () => void
  onChanged: () => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [expanded, setExpanded] = useState(expandAll)
  const [lastExpandAll, setLastExpandAll] = useState(expandAll)

  // "Alles uitklappen" follows through ("adjusting state when a prop changes").
  if (lastExpandAll !== expandAll) {
    setLastExpandAll(expandAll)
    setExpanded(expandAll)
  }

  const label = ACTIVITY_LABELS[item.type]
  const header = [
    `${label}${item.title ? ` · '${item.title}'` : ''}`,
    item.author?.name,
    relation.kind === 'organisation' && item.person ? `met ${item.person.name}` : null,
    item.edited_at ? 'bewerkt' : null,
  ]
    .filter(Boolean)
    .join(' · ')
  const plain = item.body_html ? richTextToPlain(item.body_html) : (item.legacy_text ?? '')
  const long = plain.length > CLAMP_CHARS || plain.split('\n').length > CLAMP_LINES

  // Gone at once; the server catches up, and a failure brings the line back.
  async function remove() {
    onRemove(itemKey(item))
    try {
      await deleteActivity(item.id)
    } catch (caught) {
      onReload()
      toast.error(caught instanceof Error ? caught.message : 'Verwijderen mislukt.')
      return
    }
    onChanged()
    toastUndo(`${label} verwijderd.`, async () => {
      await restoreActivity(item.id)
      onReload()
      onChanged()
    })
  }

  return (
    <Row icon={ACTIVITY_ICONS[item.type]}>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 text-sm">
            <time dateTime={item.day} className="mr-2 text-muted-foreground">
              {shortDay(item.day)}
            </time>
            <span className="font-medium break-words">{header}</span>
            {item.voice_capture_id ? (
              <span className="ml-2 inline-flex align-middle">
                <IngesprokenBadge />
              </span>
            ) : null}
          </p>
          <Popover open={menuOpen} onOpenChange={setMenuOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={`Acties voor ${label} van ${shortDay(item.day)}`}
              >
                <MoreHorizontal aria-hidden />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="flex w-40 flex-col p-1">
              <button
                type="button"
                className={menuItemClass}
                onClick={() => {
                  setMenuOpen(false)
                  setEditing(true)
                }}
              >
                Bewerken
              </button>
              <button
                type="button"
                className={cn(menuItemClass, 'text-destructive')}
                onClick={() => {
                  setMenuOpen(false)
                  void remove()
                }}
              >
                Verwijderen
              </button>
            </PopoverContent>
          </Popover>
        </div>

        {editing ? (
          <ActivityEditForm
            item={item}
            onCancel={() => setEditing(false)}
            onSaved={(updated) => {
              onReplace(updated)
              onChanged()
              setEditing(false)
            }}
          />
        ) : (
          <>
            {item.body_html ? (
              <RichTextView html={item.body_html} className={cn(long && !expanded && 'line-clamp-4')} />
            ) : item.legacy_text ? (
              <p className={cn('text-sm break-words whitespace-pre-wrap', long && !expanded && 'line-clamp-4')}>
                {item.legacy_text}
              </p>
            ) : null}
            {long ? (
              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setExpanded(!expanded)}
                className="self-start text-xs font-medium text-primary hover:underline max-md:min-h-11"
              >
                {expanded ? 'Minder' : 'Meer'}
              </button>
            ) : null}
          </>
        )}
      </div>
    </Row>
  )
}

/**
 * One line of the timeline (`<li>`): a logged activity (editable, deletable with undo), a mail
 * conversation, a completed task or a system line.
 */
export function TimelineItem({
  item,
  relation,
  expandAll,
  includeMembers,
  onReplace,
  onRemove,
  onReload,
  onChanged,
}: {
  item: TimelineItemData
  relation: RelationRef
  expandAll: boolean
  includeMembers: boolean
  onReplace: (item: TimelineItemData) => void
  onRemove: (key: string) => void
  onReload: () => void
  onChanged: () => void
}) {
  switch (item.kind) {
    case 'activity':
      return (
        <ActivityLine
          item={item}
          relation={relation}
          expandAll={expandAll}
          onReplace={onReplace}
          onRemove={onRemove}
          onReload={onReload}
          onChanged={onChanged}
        />
      )
    case 'mail_thread':
      return (
        <Row icon={Mail}>
          <MailThreadItem
            thread={item}
            relation={relation}
            includeMembers={includeMembers}
            expandAll={expandAll}
            showPersons={relation.kind === 'organisation'}
          />
        </Row>
      )
    case 'task':
      return (
        <Row icon={CheckCircle2}>
          <p className="min-w-0 flex-1 text-sm">
            <time dateTime={item.day} className="mr-2 text-muted-foreground">
              {shortDay(item.day)}
            </time>
            <span className="font-medium break-words">
              {`Taak afgerond · ${item.title}${item.assignee ? ` · ${item.assignee.name}` : ''}`}
            </span>
          </p>
        </Row>
      )
    case 'system':
      return (
        <li className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="grid size-7 shrink-0 place-items-center">
            <Info className="size-3.5" aria-hidden />
          </span>
          <p className="min-w-0 break-words">
            <time dateTime={item.day} className="mr-2">
              {shortDay(item.day)}
            </time>
            {item.link ? (
              <Link
                to={item.link}
                className="font-medium text-primary hover:underline max-md:inline-flex max-md:min-h-11 max-md:items-center"
              >
                {item.label}
              </Link>
            ) : (
              <span>{item.label}</span>
            )}
          </p>
        </li>
      )
  }
}
